import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import { BUSINESS, SERVICES } from "@/lib/site";

function FacebookIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="#1877F2" aria-hidden="true" focusable="false">
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.88v2.26h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z" />
    </svg>
  );
}

function GoogleG({ className }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-charcoal text-white">
      <div className="grain absolute inset-0" />
      <div className="container-x relative py-20 lg:py-24">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Link to="/" data-testid="footer-logo" aria-label="NorthWork Cyprus — home" className="inline-block">
              <img
                src="/images/logo-footer.webp"
                alt="NorthWork Cyprus — Air Conditioning Services"
                loading="lazy"
                className="h-14 w-auto sm:h-16"
              />
            </Link>
            <a
              href={`tel:${BUSINESS.phoneTel}`}
              data-testid="footer-phone"
              className="mt-6 block font-heading text-4xl font-extrabold tracking-tight text-white transition-colors hover:text-brand sm:text-5xl"
            >
              {BUSINESS.phoneDisplay}
            </a>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-white/70">
              Professional air-conditioning installation, deep cleaning, repair,
              diagnostics and gas refill across Paphos and surrounding areas.
            </p>
            <div className="mt-8 flex items-center gap-2 text-sm text-white/75">
              <MapPin className="h-4 w-4 text-brand" />
              {BUSINESS.area}
            </div>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61587147663725"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit NorthWork Cyprus on Facebook"
                data-testid="footer-facebook"
                className="group inline-flex w-fit items-center gap-2.5 text-[15px] text-white/70 transition-all duration-300 hover:translate-x-0.5 hover:text-white"
              >
                <FacebookIcon className="h-5 w-5 shrink-0" />
                Facebook
              </a>
              <a
                href="https://share.google/16cASIoEq75Zmd5G5"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View NorthWork Cyprus on Google"
                data-testid="footer-google"
                className="group inline-flex w-fit items-center gap-2.5 text-[15px] text-white/70 transition-all duration-300 hover:translate-x-0.5 hover:text-white"
              >
                <GoogleG className="h-5 w-5 shrink-0" />
                View on Google
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 lg:col-start-7">
            <span className="font-mono-t text-[11px] uppercase tracking-[0.22em] text-white/50">
              Services
            </span>
            <ul className="mt-6 flex flex-col gap-3.5">
              {SERVICES.map((s) => (
                <li key={s.key}>
                  <Link
                    to={s.path}
                    data-testid={`footer-link-${s.key}`}
                    className="group inline-flex items-center gap-1.5 text-[15px] text-white/80 transition-all duration-300 hover:translate-x-0.5 hover:text-brand"
                  >
                    {s.title}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <span className="font-mono-t text-[11px] uppercase tracking-[0.22em] text-white/50">
              Company
            </span>
            <ul className="mt-6 flex flex-col gap-3.5">
              <li>
                <Link to="/" className="text-[15px] text-white/80 transition-colors duration-300 hover:text-brand">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-[15px] text-white/80 transition-colors duration-300 hover:text-brand">
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[15px] text-white/80 transition-colors duration-300 hover:text-brand">
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-quote"
                  className="text-[15px] text-white/80 transition-colors duration-300 hover:text-brand"
                >
                  Request a Quote
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {BUSINESS.fullName}. All rights reserved.
          </p>
          <a
            href="https://northworkcyprus.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-website"
            className="font-mono-t text-[12px] tracking-wide text-white/60 transition-colors duration-300 hover:text-brand"
          >
            {BUSINESS.website}
          </a>
        </div>
      </div>
    </footer>
  );
}
